import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { OpeningHours, TimeRange, Weekday } from '@/types/bookings'
import { WEEKDAYS, weekdayName } from './bookingsUtils'

interface Props {
  value: OpeningHours | null
  onChange: (value: OpeningHours) => void
  disabled?: boolean
  className?: string
}

const NEW_RANGE: TimeRange = { from: '08:00', to: '18:00' }

/**
 * The weekly opening hours: one row per weekday, a switch to open it and one or more time ranges.
 * A closed day is a day with no range. Used by the space setup and by the resource's hours tab.
 */
export const WeeklyHoursEditor = ({ value, onChange, disabled = false, className }: Props) => {
  const { t, i18n } = useTranslation('bookings')
  const hours: OpeningHours = value ?? {}

  const setDay = (day: Weekday, ranges: TimeRange[]) => onChange({ ...hours, [day]: ranges })
  const setRange = (day: Weekday, index: number, patch: Partial<TimeRange>) =>
    setDay(day, (hours[day] ?? []).map((r, i) => (i === index ? { ...r, ...patch } : r)))

  return (
    <div className={cn('divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white', className)} data-testid="weekly-hours">
      {WEEKDAYS.map((day) => {
        const ranges = hours[day] ?? []
        const open = ranges.length > 0
        const name = weekdayName(day, i18n.language)
        return (
          <div key={day} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:gap-4">
            <div className="flex items-center gap-3 sm:w-48 sm:flex-shrink-0">
              <Switch
                checked={open}
                disabled={disabled}
                aria-label={t('hours.openOn', { day: name })}
                onCheckedChange={(on) => setDay(day, on ? [{ ...NEW_RANGE }] : [])}
              />
              <span className="text-sm font-medium text-gray-900 first-letter:uppercase">{name}</span>
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              {!open && <p className="py-1.5 text-sm text-gray-500">{t('hours.closed')}</p>}
              {ranges.map((range, index) => (
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <Input
                    type="time"
                    value={range.from}
                    disabled={disabled}
                    aria-label={t('hours.from', { day: name })}
                    className="w-32"
                    onChange={(e) => setRange(day, index, { from: e.target.value })}
                  />
                  <span className="text-sm text-gray-500">{t('hours.to')}</span>
                  <Input
                    type="time"
                    value={range.to}
                    disabled={disabled}
                    aria-label={t('hours.until', { day: name })}
                    className="w-32"
                    onChange={(e) => setRange(day, index, { to: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    aria-label={t('hours.removeRange', { day: name })}
                    onClick={() => setDay(day, ranges.filter((_, i) => i !== index))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {open && (
                <Button type="button" variant="ghost" size="sm" className="gap-1 px-2 text-primary" disabled={disabled}
                  onClick={() => setDay(day, [...ranges, { from: '14:00', to: '18:00' }])}>
                  <Plus className="h-3.5 w-3.5" />
                  {t('hours.addRange')}
                </Button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
